import { cancelable, CancelablePromise } from 'cancelable-promise';
import Immutable from 'immutable';
import 'isomorphic-fetch';
import pako from 'pako';
import { getServiceUrl } from '../api.config';
import { ResponseWithData } from '../types';
import { ApiClientOptions } from '../types/api';
import {
  getAccessToken,
  getAccessTokenByPriority,
  getRefreshToken,
  getRefreshTokenByPriority,
  postLogoutEvent,
  setAccessToken,
  setAccessTokenByPriority,
} from './authentication';
import { getAppConfig } from './env';
import { getPriority } from './priority';

let refreshTokenPromise: CancelablePromise<any> | null = null;

interface ExtraHeader {
  key?: string;
  value?: string;
}

interface ResponseData {
  data?: any;
}

interface PromiseCache {
  timestamp: number;
  promise: CancelablePromise<any>;
}

interface CallApiOptions {
  url: string;
  parameters?: { [key: string]: any };
  useCache?: boolean;
  withRefreshToken?: boolean;
  withBlob?: boolean;
}

export default class ApiClient {
  public host: string;
  public cache: { [url: string]: PromiseCache | null | undefined };
  public cacheEnabled: boolean;
  public acceptLanguage: ApiClientOptions['acceptLanguage'];
  public immutable: boolean;
  public timeout: number;
  public alkPriority?: string;
  public defaultTargetMarketId?: number;
  public useAdminToken: boolean;

  public constructor(host: string, options: ApiClientOptions = {}) {
    this.host = host || '';
    this.cache = {};
    this.cacheEnabled = options.cacheEnabled || false;
    this.acceptLanguage = options.acceptLanguage || 'fr-FR';
    this.immutable = options.immutable || false;
    this.timeout = options.cacheTimeout || 5000;
    this.alkPriority = options.alkPriority;
    this.useAdminToken = options.useAdminToken === true;
  }

  public getBlob<T = any, R = ResponseWithData<T>>(
    path: string,
    parameters: string = '',
    data: object = {},
    post: boolean = false,
  ): CancelablePromise<R> {
    const url = `${this.host}${path}${parameters}`;
    if (post) {
      // Support for getting blob via a post API (ie: too many parameters to be sent as a GET).
      const headers = new Headers();
      headers.set('Content-Type', 'application/json;charset=UTF-8');
      const body = JSON.stringify(data);
      return this.callApiBlob<T, R>({
        parameters: {
          body,
          headers,
          method: 'post',
        },
        url,
      });
    }
    return this.callApiBlob<T, R>({
      parameters: { method: 'get' },
      url,
    });
  }

  public get<T = any, R = ResponseWithData<T>>(
    path: string,
    parameters: string = '',
    useCache: boolean = true,
    extraHeaders?: ExtraHeader[],
  ): CancelablePromise<R> {
    const headers = new Headers();
    if (extraHeaders && extraHeaders.length) {
      extraHeaders.forEach(({ key, value }) => {
        if (key && value) {
          headers.set(key, value);
        }
      });
    }
    return this.callApi<T, R>({
      parameters: { headers, method: 'get' },
      url: `${this.host}${path}${parameters}`,
      useCache,
    });
  }

  public post<T = any, R = ResponseWithData<T>>(
    path: string,
    data: any = {},
    file?: any,
    extraHeaders?: ExtraHeader[],
  ): CancelablePromise<R> {
    const headers = new Headers();
    let body: any;
    if (file) {
      body = new FormData();
      Object.keys(data).forEach((key) => {
        body.append(key, data[key]);
      });
      body.append('file', file);
    } else {
      headers.set('Content-Type', 'application/json;charset=UTF-8');
      if (extraHeaders && extraHeaders.length) {
        extraHeaders.forEach(({ key, value }) => {
          if (key && value) {
            headers.set(key, value);
          }
        });
      }
      body = this.compress(data, headers);
    }
    return this.callApi<T, R>({
      parameters: { body, headers, method: 'post' },
      url: `${this.host}${path}`,
    });
  }

  public put<T = any, R = ResponseWithData<T>>(
    path: string,
    data: any = {},
    extraHeaders?: ExtraHeader[],
  ): CancelablePromise<R> {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json;charset=UTF-8');
    if (extraHeaders && extraHeaders.length) {
      extraHeaders.forEach(({ key, value }) => {
        if (key && value) {
          headers.set(key, value);
        }
      });
    }
    const body = this.compress(data, headers);
    return this.callApi<T, R>({
      parameters: { body, headers, method: 'put' },
      url: `${this.host}${path}`,
    });
  }

  public delete<T = any, R = ResponseWithData<T>>(
    path: string,
    data: any = {},
    extraHeaders?: ExtraHeader[],
  ): CancelablePromise<R> {
    const headers = new Headers();
    headers.set('Content-Type', 'application/json;charset=UTF-8');
    if (extraHeaders && extraHeaders.length) {
      extraHeaders.forEach(({ key, value }) => {
        if (key && value) {
          headers.set(key, value);
        }
      });
    }
    const body = JSON.stringify(data);
    return this.callApi<T, R>({
      parameters: { body, headers, method: 'delete' },
      url: `${this.host}${path}`,
    });
  }

  public formatParams(p: { [key: string]: any }) {
    const params = p;
    const headers = params.headers || new Headers();

    headers.set('Authorization', `Bearer ${this.getAccessToken()}`);
    headers.set(
      'Accept-Language',
      typeof this.acceptLanguage === 'function'
        ? this.acceptLanguage()
        : this.acceptLanguage,
    );
    headers.set('Cache-Control', 'no-cache');
    headers.set('Pragma', 'no-cache');
    headers.set('Expires', -1);
    headers.set('X-Alk-Priority', `${this.alkPriority || getPriority()}`);
    params.headers = headers;
    return params;
  }

  public getError(response: Response, url: string, params: any, res: any) {
    switch (response.status) {
      case 401:
        return this.formatApiError(
          `api.route.unauthorized - ${url}`,
          url,
          params,
          res,
        );
      case 402:
        return this.formatApiError(
          `api.route.paymentRequired - ${url}`,
          url,
          params,
          res,
        );
      case 403:
        return this.formatApiError(
          `api.route.forbidden - ${url}`,
          url,
          params,
          res,
        );
      case 404:
        return this.formatApiError(
          `api.route.notFound - ${url}`,
          url,
          params,
          res,
        );
      case 502:
        return this.formatApiError(
          `api.route.serverError - ${url}`,
          url,
          params,
          res,
        );
      default:
        return this.formatApiError(
          `api.route.error - ${url}`,
          url,
          params,
          res,
        );
    }
  }

  public formatApiError(name: string, url: string, params: any, error: any) {
    return {
      _type: '_api',
      error: error || null,
      name,
      params: params || {},
      url,
    };
  }

  public formatResponse(response: any = {}, raw: any = {}) {
    const data: ResponseData = raw;
    if (data.data && this.immutable) {
      data.data = Immutable.fromJS(data.data);
    }
    return {
      data,
      headers: response.headers,
      status: response.status,
    };
  }

  public throwError(response: any, data: any, url: string, params: any) {
    const res = {
      body: data,
      status: response.status,
      statusText: response.statusText,
    };
    return this.getError(response, url, params, res);
  }

  public getCacheStatus<T = any, R = ResponseWithData<T>>(
    url: string,
    params: any = {},
    useCache: boolean = true,
  ): { hasCache: boolean; promise?: CancelablePromise<R> | null } {
    let hasCache = false;
    let promise: CancelablePromise<R> | null = null;
    const cache = this.cache[url];
    if (
      this.cacheEnabled &&
      params.method === 'get' &&
      cache &&
      cache.timestamp > Date.now() - this.timeout &&
      useCache
    ) {
      if (cache.promise.isCanceled()) {
        this.cache[url] = null;
      } else {
        hasCache = true;
        promise = cache.promise as CancelablePromise<R>;
      }
    }
    return { hasCache, promise };
  }

  public shouldRefreshToken(status: number, withRefreshToken: boolean) {
    return status === 401 && withRefreshToken && this.getRefreshToken() != null;
  }

  public async responseToJson(response: any, defaultData: any = {}) {
    try {
      const json = await response.json();
      return json;
    } catch (error) {
      return defaultData;
    }
  }

  public callApi<T = any, R = ResponseWithData<T>>({
    url,
    parameters = {},
    useCache = true,
    withRefreshToken = true,
    withBlob = false,
  }: CallApiOptions): CancelablePromise<R> {
    if (!withBlob) {
      const cacheStatus = this.getCacheStatus<T, R>(url, parameters, useCache);
      if (cacheStatus.hasCache && cacheStatus.promise) {
        return cacheStatus.promise;
      }
    }
    const params = this.formatParams(parameters);
    const promise = cancelable<R>(
      new Promise(
        // eslint-disable-next-line no-async-promise-executor
        async (resolve, reject) => {
          try {
            const response = await fetch(url, params);
            const { status } = response;
            if (status === 204) {
              resolve(response as any);
            } else if (status < 400) {
              if (withBlob) {
                const data = await response.blob();
                const res = {
                  data,
                  headers: response.headers,
                  status: response.status,
                };
                resolve(res as any);
              } else {
                const data = await this.responseToJson(response);
                resolve(this.formatResponse(response, data) as any);
              }
            } else if (this.shouldRefreshToken(status, withRefreshToken)) {
              await this.refreshToken();
              const res = await this.callApi<T>({
                parameters,
                url,
                useCache: false,
                withBlob,
                withRefreshToken: false,
              });
              resolve(res as any);
            } else {
              throw response;
            }
          } catch (error) {
            const data = await this.responseToJson(error, error);
            reject(this.formatResponse(error, data));
          }
        },
      ),
    );

    if (!withBlob && this.cacheEnabled && parameters.method === 'get') {
      this.cache[url] = { promise, timestamp: Date.now() };
    }

    return promise;
  }

  public callApiBlob<T = any, R = ResponseWithData<T>>(props: CallApiOptions) {
    return this.callApi<T, R>({ ...props, withBlob: true });
  }

  public refreshToken() {
    if (refreshTokenPromise) {
      return refreshTokenPromise;
    }
    const headers = new Headers({
      'Content-Type': 'application/json;charset=UTF-8',
    });
    const body = JSON.stringify({
      grant_type: 'refresh_token',
      refresh_token: this.getRefreshToken(),
    });
    refreshTokenPromise = this.callApi({
      parameters: { body, headers, method: 'post' },
      url: `${getServiceUrl('auth')}/auth/v2/token`,
      useCache: false,
      withRefreshToken: false,
    }).then(
      (response: any) => {
        this.setAccessToken(response.data.access_token);
        refreshTokenPromise = null;
      },
      (error: any) => {
        if (error.status === 401) {
          postLogoutEvent();
        }
        refreshTokenPromise = null;
        return Promise.reject(error);
      },
    );
    return refreshTokenPromise;
  }

  public compress(bodyData: pako.Data, headers: Headers): string | pako.Data {
    let body: string | pako.Data = JSON.stringify(bodyData);
    if (
      getAppConfig('compress') === true &&
      !/compress=false/i.test(document.location.search)
    ) {
      headers.set('Content-Encoding', 'gzip');
      body = pako.gzip(body);
    }
    return body;
  }

  public getAccessToken() {
    return this.useAdminToken ? getAccessToken() : getAccessTokenByPriority();
  }

  public getRefreshToken() {
    return this.useAdminToken ? getRefreshToken() : getRefreshTokenByPriority();
  }

  public setAccessToken(accessToken: string) {
    if (this.useAdminToken) {
      setAccessToken(accessToken);
    } else {
      setAccessTokenByPriority(accessToken);
    }
  }
}
